import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';

import { Search, RefreshCcw, AlertCircle, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getRecentEmails } from '../../services/google/gmail';
import { EmailMessage } from '../../services/google/gmail';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useToast } from '../ui/use-toast';
import { EmailActions } from './EmailActions';
import { ObjectDrawer } from './ObjectDrawer';

export const EmailList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const locale = i18n.language === 'fr' ? fr : enUS;
  
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // État pour le drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  // Fonction pour charger les emails
  const loadEmails = async (query = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const fetchedEmails = await getRecentEmails(20, query);
      setEmails(fetchedEmails);
    } catch (err) {
      console.error('Error fetching emails:', err);
      setError(t('emails.fetchError', 'Impossible de récupérer vos emails'));
    } finally {
      setLoading(false);
    }
  };

  // Charger les emails au chargement du composant
  useEffect(() => {
    loadEmails();
  }, []);

  // Gérer la sélection des emails
  const toggleSelectEmail = (emailId: string) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId) 
        : [...prev, emailId]
    );
  };
  
  // Fonction pour gérer la recherche
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadEmails(searchQuery);
  };

  // Extraire l'expéditeur et le sujet d'un email
  const extractEmailInfo = (email: EmailMessage) => {
    const subject = email.payload?.headers?.find(h => h.name === 'Subject')?.value || 'No Subject';
    const from = email.payload?.headers?.find(h => h.name === 'From')?.value || 'Unknown Sender';
    
    // Extraire le nom de l'expéditeur si possible
    const senderMatch = from.match(/"(.+?)"|\s*([^<]+?)\s*(?:<|$)/);
    const sender = senderMatch ? (senderMatch[1] || senderMatch[2]).trim() : from;
    
    return { subject, from, sender };
  };

  // Formater la date d'un email
  const formatEmailDate = (dateString: string) => {
    const date = new Date(parseInt(dateString));
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  // Fonction pour ouvrir le drawer avec un email sélectionné
  const openEmailDrawer = (emailId: string, threadId: string) => {
    setSelectedEmailId(emailId);
    setSelectedThreadId(threadId);
    setDrawerOpen(true);
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle>{t('emails.recentTitle', 'Emails Récents')}</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => loadEmails(searchQuery)}
              disabled={loading}
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              {t('common.refresh', 'Actualiser')}
            </Button>
          </div>
          
          <form onSubmit={handleSearch} className="flex items-center space-x-2">
            <Input
              placeholder={t('emails.searchPlaceholder', 'Rechercher dans les emails...')}
              className="flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button type="submit" variant="secondary" disabled={loading}>
              <Search className="h-4 w-4" />
            </Button>
          </form>
        </CardHeader>
        
        <Separator />
        
        <CardContent className="pt-4 px-0">
          {error && (
            <div className="px-6 py-3 text-sm text-red-500">
              <AlertCircle className="inline h-4 w-4 mr-1" />
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="space-y-2 px-6">
              {Array(5).fill(null).map((_, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-4 rounded-sm" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : emails.length === 0 ? (
            <div className="px-6 py-8 text-center text-muted-foreground">
              <div className="mb-2">
                <Inbox className="mx-auto h-10 w-10" />
              </div>
              <p>{t('emails.noEmails', 'Aucun email trouvé')}</p>
            </div>
          ) : (
            <>
              {/* Actions groupées */}
              {selectedEmails.length > 0 && (
                <div className="bg-muted/40 px-6 py-2 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {t('emails.selected', { count: selectedEmails.length, defaultValue: '{{count}} email(s) sélectionné(s)' })}
                  </span>
                  <div className="flex space-x-2">
                    <EmailActions 
                      emailIds={selectedEmails}
                      onSuccess={() => {
                        toast({
                          title: t('emails.actionSuccess', 'Action réussie'),
                          description: t('emails.actionSuccessDescription', 'Les emails sélectionnés ont été traités'),
                        });
                        setSelectedEmails([]);
                        loadEmails();
                      }}
                    />
                  </div>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center"></TableHead>
                    <TableHead>{t('emails.from', 'De')}</TableHead>
                    <TableHead>{t('emails.subject', 'Sujet')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('emails.date', 'Date')}</TableHead>
                    <TableHead className="w-20 text-right">{t('emails.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map(email => {
                    const { subject, sender } = extractEmailInfo(email);
                    const isUnread = email.labelIds?.includes('UNREAD');
                    const isSelected = selectedEmails.includes(email.id);
                    
                    return (
                      <TableRow 
                        key={email.id} 
                        className={`${isSelected ? 'bg-muted/40' : ''} cursor-pointer hover:bg-muted/50`}
                        onClick={(e) => {
                          // Empêcher le clic si on a cliqué sur une case à cocher ou un bouton d'action
                          if (
                            (e.target as HTMLElement).tagName === 'INPUT' || 
                            (e.target as HTMLElement).closest('button')
                          ) {
                            return;
                          }
                          openEmailDrawer(email.id, email.threadId);
                        }}
                      >
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => toggleSelectEmail(email.id)}
                            className="w-4 h-4"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {/* Indicateur visuel pour les emails non lus */}
                            {isUnread && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" 
                                  aria-label={t('emails.unread', 'Non lu')} />
                            )}
                            <div className={`font-medium max-w-[200px] truncate ${isUnread ? 'font-semibold' : ''}`}>
                              {sender}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={`max-w-md truncate ${isUnread ? 'font-semibold' : ''}`}>
                            {subject}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {formatEmailDate(email.internalDate)}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <EmailActions 
                            emailIds={[email.id]} 
                            threadId={email.threadId}
                            emailSubject={subject}
                            onSuccess={() => loadEmails()}
                            compact={true}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Drawer pour afficher le contenu de l'email */}
      {selectedEmailId && selectedThreadId && (
        <ObjectDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          objectType="email"
          objectId={selectedEmailId}
          threadId={selectedThreadId}
        />
      )}
    </>
  );
};
